import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useEffect } from 'react';
import useAnalyticsContext from '|/hooks/use-analytics-context';

let previousEvent = {
  previousEventId: '',
  previousTime: {} as Date
};

function safeParse<T = object>(input: string): T | undefined {
  try {
    return JSON.parse(input) as T;
  } catch (ex) {
    return undefined;
  }
}

export default function useTrackClickEvents() {
  const { trackEvent } = useAnalyticsContext();

  const trackClickEvents = useCallback(
    (ev: MouseEvent) => {
      const { previousEventId, previousTime } = previousEvent;
      if (!(ev.target instanceof Element)) return;

      const target = ev.target.closest<HTMLElement>('[data-analytics-id]');
      const { analyticsId, analyticsAttributes } = target?.dataset ?? {};

      if (!analyticsId) return;
      if (analyticsId === previousEventId && Date.now() - previousTime.getTime() < 500) {
        previousEvent = {
          previousEventId: analyticsId,
          previousTime: new Date()
        };

        return;
      }

      previousEvent = {
        previousEventId: analyticsId,
        previousTime: new Date()
      };

      let scopeMetadata: object = {};
      let node: HTMLElement | null = target;
      while (node) {
        const scopeJson = node.dataset.analyticsScope;
        if (scopeJson) {
          const parsed = safeParse(scopeJson);
          if (parsed) scopeMetadata = { ...parsed, ...scopeMetadata };
        }
        node = node.parentElement;
      }

      const eventSpecificAttributes = analyticsAttributes ? safeParse(analyticsAttributes) : {};
      const ddContext = datadogRum.getInternalContext();
      const attributesToSend = {
        ...eventSpecificAttributes,
        analyticsId: analyticsId,
        datadogSessionId: ddContext?.session_id,
        datadogViewId: ddContext?.view?.id,
        ...(Object.keys(scopeMetadata).length > 0 && { metadata: scopeMetadata })
      };

      trackEvent('pp_click', attributesToSend);

      datadogRum.addAction(`${analyticsId}-clicked`);
    },
    [trackEvent]
  );

  useEffect(() => {
    function onClick(ev: MouseEvent) {
      if (!(ev.target instanceof Element)) return;

      trackClickEvents(ev);
    }

    document.addEventListener('click', onClick, false);

    return () => {
      document.removeEventListener('click', onClick);
    };
  }, [trackClickEvents]);

  return trackClickEvents;
}
